import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import axios, { type AxiosError } from 'axios'
import { isObjectLike } from 'lodash-es'
import qs from 'qs'

import { TOAST_STR } from '@/constant'
import { CommonError } from '@/dto'
import WebviewService from '@/service/webview.service.ts'
import type { ResErr } from '@/types/common/api'
import { deleteNullField } from '@/utils/common'
import { envUtils } from '@/utils/envUtils.ts'
import { reporter } from '@/utils/modules/errorReporting/reporter'
import { toastError } from '@/utils/toast'

import { wrapErrorHandlerWithJwt, wrapRequestInterceptorWithJwt } from './jwtInterceptors'

const BASE_URL = (() => {
  if (envUtils.isLive) {
    return `https://sai.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isStaging) {
    return `https://sai-staging.${domainProvider.TARGET_DOMAIN}`
  }

  return `https://sai-dev.${domainProvider.TARGET_DOMAIN}`
})()

const XPlatform = (() => {
  if (WebviewService.isAndroid) return 'TEACHER_ANDROID'
  if (WebviewService.isIpad) return 'TEACHER_IOS'
  return 'TEACHER_WEB'
})()

export const saiAxios = axios.create({
  baseURL: BASE_URL,
  headers: {
    'x-platform': XPlatform,
    'x-freewheelin-host': envUtils.isSchool ? 'schoolflat.com' : domainProvider.TARGET_DOMAIN,
  },
  paramsSerializer: (params) => {
    // params가 null인 값은 서버로 보내지 않음
    const newParams = isObjectLike(params) ? deleteNullField(params) : params
    return qs.stringify(newParams, { arrayFormat: 'comma' })
  },
})

saiAxios.interceptors.request.use(wrapRequestInterceptorWithJwt((config) => config))

const saiJwtErrorHandler = wrapErrorHandlerWithJwt(
  async (error: unknown) => {
    const axiosError = error as AxiosError<ResErr>

    if (axiosError.response) {
      const {
        config,
        data: { message, code, data },
        status,
      } = axiosError.response
      const err = new CommonError(message, { type: 'API', status, code, data, config })

      if (status === 500) {
        toastError(TOAST_STR.errorMessage)
      }

      reporter.capture(err, { mechanism: 'interceptor' })
      return Promise.reject<CommonError>(err)
    }

    reporter.capture(axiosError, { mechanism: 'interceptor' })
    return Promise.reject(axiosError)
  },
  async (originalRequest, newAccessToken) => {
    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
    return saiAxios(originalRequest)
  },
)

saiAxios.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const axiosError = error as AxiosError
    // SAI 서버가 401 응답 시 CORS 헤더를 포함하지 않아
    // 브라우저가 응답을 차단하면 ERR_NETWORK로 처리됨.
    // JWT retry 로직이 작동하도록 가짜 401 응답으로 변환
    if (!axiosError.response && axiosError.code === 'ERR_NETWORK') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(axiosError as any).response = {
        status: 401,
        statusText: 'Unauthorized',
        data: { message: 'Network Error', code: 'ERR_NETWORK', data: null },
        headers: {},
        config: axiosError.config,
      }
    }
    return saiJwtErrorHandler(error)
  },
)
